import React from 'react';
import { motion } from 'framer-motion';

const HolidayShoppingGuiltLanding = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Holiday Shopping Guilt: Find Joy in Giving Without the Stress
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Holiday Shopping Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your holiday purchases, manage budget stress, and enjoy a season of mindful giving without the financial anxiety.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Holiday Purchase
            </motion.button>
          </div>
          <div className="text-8xl">🎁</div>
        </div>
      </div>
      
      {/* What causes holiday shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Holiday Shopping Creates Extra Guilt</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Financial Pressure</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The expectation to buy gifts for everyone strains budgets and leads to January regret.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🎄</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Expectations</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Pressure to find "perfect" gifts and match others' spending leads to emotional buying.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏰</span>
              <div>
                <h3 className="font-bold dark:text-white">Time Scarcity</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Last-minute shopping leads to impulse buys, overspending, and post-holiday regret.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🛍️</span>
              <div>
                <h3 className="font-bold dark:text-white">Self-Gifting</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  "One for them, one for me" mentality creates additional unnecessary spending.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Holiday Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Holiday Budget
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Set realistic spending limits for each person and monitor your progress in real-time.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Identify Emotional Holiday Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Recognize when festive pressure, holiday sales, or comparison with others drives overspending.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Personalized Holiday Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive tailored strategies for meaningful gift-giving that won't break your budget.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Track Your Post-Holiday Recovery
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Analyze January impact and plan a faster financial recovery after the holiday season.
            </p>
          </div>
        </div>
      </section>
      
      {/* Holiday Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What Holiday Shopper Type Are You?</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎯</span>
              <h3 className="font-bold mt-2 dark:text-white">The Last-Minute Dasher</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You wait until December 23rd, then panic-buy expensive gifts because time has run out for better options.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📊</span>
              <h3 className="font-bold mt-2 dark:text-white">The Comparison Shopper</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You stress about matching others' gift values and worry what people will think about your presents.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">✨</span>
              <h3 className="font-bold mt-2 dark:text-white">The Perfectionist</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You obsess over finding the "perfect" gift for everyone, often spending more than intended in the process.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">The Mindful Gifter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You plan ahead, set realistic budgets, and focus on thoughtful gifts rather than price tags.
            </p>
          </div>
        </div>
      </section>
      
      {/* Holiday Shopping Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Holiday Shopping By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">$1,463</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average holiday spending per consumer</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">64%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of shoppers go over their holiday budget</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">78%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">experience increased financial stress during holidays</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3.2</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">months to pay off average holiday debt</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Managed Holiday Spending Guilt</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "Last year I ended up $2,000 in debt after Christmas. This year I tracked every purchase with Guilt Gauge and stuck to my budget. January doesn't feel like a financial disaster for once!"
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Michael R.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Last-Minute Dasher</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to obsess over buying everyone the 'perfect' gift regardless of cost. The guilt calculator helped me prioritize thoughtfulness over price tag, and my holiday stress has disappeared."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sarah T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovering Perfectionist</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Holiday Shopping Guilt: Common Questions</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I avoid holiday overspending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create a realistic holiday budget before shopping starts, allocating specific amounts for each person. Track each purchase with our guilt calculator, and consider alternatives like homemade gifts, experiences, or gift exchanges to reduce the number of presents you need to buy.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty about holiday shopping?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Holiday guilt often comes from financial strain, comparing your gifts to others, feeling pressured to find "perfect" presents, and the contrast between material consumption and the season's intended meaning. Our calculator helps identify your specific triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much should I spend on holiday gifts?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial experts recommend spending no more than 1-1.5% of your annual income on holiday gifts. Our guilt calculator helps you determine what spending pattern feels good both emotionally and financially, based on your personal situation.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I enjoy gift-giving without the guilt?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Focus on thoughtfulness rather than price, set clear expectations with loved ones about spending limits, shop early to avoid panic buying, and track your holiday spending with our guilt calculator to stay mindful of your budget and emotional triggers.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Enjoy a Guilt-Free Holiday Season</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your holiday purchases and transform your relationship with gift-giving.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Holiday Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Holiday Shopping Guilt</h2>
          <p className="mb-3">
            Holiday shopping guilt is the emotional discomfort experienced during and after the gift-giving season, often stemming from financial strain, last-minute panic purchases, and the pressure to find perfect presents that will impress recipients.
          </p>
          <p className="mb-3">
            Our Holiday Guilt Calculator helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to enjoy a meaningful gift-giving season without the financial anxiety.
          </p>
          <p>
            Whether you're dealing with budget stress, gift comparison anxiety, or concerns about post-holiday debt, our free tool helps you develop healthier holiday shopping habits and reduce the guilt that often accompanies this festive season.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default HolidayShoppingGuiltLanding;
